import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Form } from "react-bootstrap";
import { Line } from "react-chartjs-2";
import dateFormat from "dateformat";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { FaChartLine } from "react-icons/fa";
import "chart.js/auto";
import DashMedidasSectionCard from "../../DashMedidas/components/DashMedidasSectionCard/DashMedidasSectionCard";
import DashMedidasButton, {
  DashMedidasButtonGroup,
} from "../../DashMedidas/components/DashMedidasButton/DashMedidasButton";
import {
  collectAvailableModelsFromSerials,
  getEquipmentColor,
  getEquipmentModel,
  getMeasurementSerial,
  serialMatchesModels,
  serialsMatch,
} from "../utils/equipmentCompare";
import {
  MAX_COMPARE_SERIES,
  resolveCompareSelection,
} from "../utils/compareSelection";
import {
  buildMetadataLabelsMap,
  buildMetadataPointStylePropsForLabels,
  createMetadataTooltipCallbacks,
} from "../../../utils/chartMeasureMetadata";

const COMPARE_BY_EQUIPMENT = "equipment";
const COMPARE_BY_UNIT = "unit";
const CHART_HEIGHT_DESKTOP = 360;
const CHART_HEIGHT_MOBILE = 420;

const formatLabel = (date) => dateFormat(date, "UTC:dd/mm/yy' 'HH:MM");

const parseMeasurementNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  const normalized = String(value).trim().replace(",", ".");
  if (!normalized) return null;

  const number = Number(normalized);
  return Number.isFinite(number) ? number : null;
};

const getHumidityValue = (item) => {
  const candidates = [
    item?.humidityReal,
    item?.humidity,
    item?.umidade,
  ];

  for (const candidate of candidates) {
    const value = parseMeasurementNumber(candidate);
    if (value !== null) return value;
  }

  return null;
};

const getTemperatureValue = (item) => {
  const candidates = [
    item?.temperatureReal,
    item?.temperature,
    item?.temperatura,
  ];

  for (const candidate of candidates) {
    const value = parseMeasurementNumber(candidate);
    if (value !== null) return value;
  }

  return null;
};

const buildScaleOptions = (values) => {
  let minValue = Infinity;
  let maxValue = -Infinity;
  let hasNumber = false;

  for (let i = 0; i < values.length; i += 1) {
    const number = parseMeasurementNumber(values[i]);
    if (number === null) continue;
    hasNumber = true;
    if (number < minValue) minValue = number;
    if (number > maxValue) maxValue = number;
  }

  if (!hasNumber) {
    return { min: 0, max: 1, stepSize: 5 };
  }

  minValue -= (Math.abs(minValue) * 30) / 100 || 1;
  maxValue += (Math.abs(maxValue) * 30) / 100 || 1;
  if (minValue === maxValue) {
    minValue -= 1;
    maxValue += 1;
  }
  return { min: minValue, max: maxValue, stepSize: 5 };
};

const buildCompareDataLabels = (suffix = "", color = "#4a5c52") => ({
  display: "auto",
  align: "top",
  anchor: "end",
  offset: 2,
  color,
  clamp: true,
  font: {
    weight: 600,
    size: 11,
  },
  formatter: (value) => {
    if (value === null || value === undefined || value === "" || Number.isNaN(Number(value))) {
      return "";
    }
    return `${value}${suffix}`;
  },
});

const buildChartTooltipOptions = (callbacks = {}) => ({
  enabled: true,
  backgroundColor: "rgba(28, 66, 32, 0.72)",
  titleColor: "rgba(255, 255, 255, 0.96)",
  bodyColor: "rgba(255, 255, 255, 0.9)",
  footerColor: "rgba(255, 255, 255, 0.78)",
  borderColor: "rgba(143, 173, 21, 0.42)",
  borderWidth: 1,
  cornerRadius: 10,
  padding: { top: 10, right: 13, bottom: 10, left: 13 },
  boxPadding: 6,
  caretSize: 6,
  caretPadding: 9,
  titleFont: {
    size: 11,
    weight: "600",
    family: "'Segoe UI', system-ui, -apple-system, sans-serif",
  },
  bodyFont: {
    size: 12,
    weight: "500",
    family: "'Segoe UI', system-ui, -apple-system, sans-serif",
  },
  footerFont: {
    size: 11,
    weight: "400",
    family: "'Segoe UI', system-ui, -apple-system, sans-serif",
  },
  titleSpacing: 6,
  bodySpacing: 5,
  displayColors: true,
  usePointStyle: true,
  boxWidth: 7,
  boxHeight: 7,
  multiKeyBackground: "rgba(255, 255, 255, 0.14)",
  callbacks,
});

const buildChartOptions = (
  values,
  valueSuffix = "",
  { chartLabels = [], sourceData = [], curvaSelecionada = "" } = {}
) => {
  const y = buildScaleOptions(values);
  let delayed;

  const getGrainForIndex = (dataIndex) => {
    const pointLabel = chartLabels[dataIndex];
    const item = pointLabel
      ? sourceData.find(
          (entry) =>
            entry?.date && formatLabel(entry.date) === pointLabel
        )
      : null;

    return item?.grain || curvaSelecionada || "";
  };

  return {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y,
      x: {},
    },
    animations: {
      radius: {
        duration: 500,
        easing: "linear",
        loop: (context) => context.active,
      },
    },
    animation: {
      onComplete: () => {
        delayed = true;
      },
      delay: (context) => {
        const totalDataPoints = context.chart.data.datasets.reduce(
          (sum, dataset) => sum + dataset.data.length,
          0
        );
        if (totalDataPoints > 5000) return 0;
        let delay = 0;
        if (context.type === "data" && context.mode === "default" && !delayed) {
          delay = context.dataIndex * 5 + context.datasetIndex * 2;
        }
        return delay;
      },
    },
    hoverRadius: 6,
    hoverBackgroundColor: "yellow",
    interaction: {
      mode: "nearest",
      intersect: false,
      axis: "x",
    },
    plugins: {
      legend: {
        display: true,
        position: "top",
      },
      tooltip: buildChartTooltipOptions(
        createMetadataTooltipCallbacks({
          chartLabels,
          data: sourceData,
          primaryDatasetLabels: [],
          formatLabel,
          extraCallbacks: {
            label(context) {
              let label = context.dataset.label || "";
              if (label) label += ": ";
              if (context.parsed.y !== null && context.parsed.y !== undefined) {
                label += `${context.parsed.y}${valueSuffix}`;
              }

              const grain = getGrainForIndex(context.dataIndex);
              if (grain) {
                return [label, grain];
              }
              return label;
            },
          },
        })
      ),
      title: {
        display: true,
        text: "",
      },
      datalabels: {
        display: "auto",
      },
    },
  };
};

const averageNumbers = (values) => {
  const numbers = values
    .map(parseMeasurementNumber)
    .filter((v) => v !== null);
  if (!numbers.length) return null;
  const sum = numbers.reduce((acc, value) => acc + value, 0);
  return Number((sum / numbers.length).toFixed(1));
};

const resolveCompanyId = (item, equipmentOptions = []) => {
  if (item?.companyId) return String(item.companyId);

  const serial = getMeasurementSerial(item);
  if (!serial) return null;

  const meta = equipmentOptions.find((option) =>
    serialsMatch(option.serial, serial)
  );
  return meta?.companyId ? String(meta.companyId) : null;
};

const Grafico = ({
  data = [],
  hours,
  curvaSelecionada,
  curveOptions = [],
  onCurvaChange,
  equipmentOptions = [],
  filterContent = null,
  hasEquipmentSelection = false,
}) => {
  const [humidity, setHumidity] = useState(null);
  const [optionshumidity, setOptionshumidity] = useState(null);
  const [temperature, setTemperature] = useState(null);
  const [optionsTemperature, setOptionsTemperature] = useState(null);
  const [ph, setPh] = useState(null);
  const [optionsph, setOptionsph] = useState(null);

  const [modelFilter, setModelFilter] = useState("");
  const [compareBy, setCompareBy] = useState(COMPARE_BY_EQUIPMENT);
  const [selectedKeys, setSelectedKeys] = useState([]);

  const chartRef1 = useRef();

  const availableModels = useMemo(
    () =>
      collectAvailableModelsFromSerials(
        equipmentOptions.map((item) => item.serial).filter(Boolean)
      ),
    [equipmentOptions]
  );

  const modelSet = useMemo(
    () => (modelFilter ? new Set([modelFilter]) : new Set()),
    [modelFilter]
  );

  const equipmentsForModel = useMemo(
    () =>
      equipmentOptions.filter((item) =>
        serialMatchesModels(item.serial, modelSet)
      ),
    [equipmentOptions, modelSet]
  );

  const unitOptions = useMemo(() => {
    const map = new Map();

    equipmentsForModel.forEach((item) => {
      const companyId = String(item.companyId || "");
      if (!companyId) return;

      if (!map.has(companyId)) {
        map.set(companyId, {
          key: companyId,
          label: item.unitName || companyId,
          color: getEquipmentColor(map.size),
          count: 0,
        });
      }
      map.get(companyId).count += 1;
    });

    return [...map.values()].sort((a, b) =>
      a.label.localeCompare(b.label, "pt-BR")
    );
  }, [equipmentsForModel]);

  const pickerOptions = useMemo(() => {
    if (compareBy === COMPARE_BY_UNIT) {
      return unitOptions;
    }

    return equipmentsForModel.map((item, index) => ({
      key: item.serial,
      label: item.unitName ? `${item.label} · ${item.unitName}` : item.label,
      color: item.color || getEquipmentColor(index),
      meta: item.model,
    }));
  }, [compareBy, unitOptions, equipmentsForModel]);

  const filteredData = useMemo(() => {
    if (!data?.length) return [];

    return data.filter((item) => {
      const serial = getMeasurementSerial(item);

      if (modelFilter) {
        if (!serial) return false;
        if (getEquipmentModel(serial).code !== modelFilter) return false;
      }

      if (!selectedKeys.length) return false;

      if (compareBy === COMPARE_BY_EQUIPMENT) {
        return (
          serial && selectedKeys.some((key) => serialsMatch(serial, key))
        );
      }

      const companyId = resolveCompanyId(item, equipmentOptions);
      return companyId && selectedKeys.includes(companyId);
    });
  }, [data, modelFilter, selectedKeys, compareBy, equipmentOptions]);

  const prevOptionsCountRef = useRef(0);
  const manualSelectionRef = useRef(false);
  const pickerKeysSignature = useMemo(
    () => pickerOptions.map((item) => item.key).join("\0"),
    [pickerOptions]
  );

  // Só reage a mudança do conjunto de opções — NÃO a cada update de `data`,
  // senão a seleção manual é sobrescrita / filtrada e o gráfico fica vazio.
  useEffect(() => {
    const validKeys = pickerOptions.map((item) => item.key);
    const previousOptionsCount = prevOptionsCountRef.current;
    const optionsGrew = validKeys.length > previousOptionsCount;
    const largePicker = validKeys.length > MAX_COMPARE_SERIES;

    if (largePicker && optionsGrew) {
      manualSelectionRef.current = false;
    }

    prevOptionsCountRef.current = validKeys.length;

    const keysWithData = validKeys.filter((key) => {
      if (!data?.length) return false;
      if (compareBy === COMPARE_BY_EQUIPMENT) {
        return data.some((item) =>
          serialsMatch(getMeasurementSerial(item), key)
        );
      }
      return data.some(
        (item) => resolveCompanyId(item, equipmentOptions) === key
      );
    });

    setSelectedKeys((prev) => {
      const next = resolveCompareSelection({
        validKeys,
        keysWithData,
        previousKeys: prev,
        previousOptionsCount,
        maxSeries: MAX_COMPARE_SERIES,
        allowKeepOnLarge: manualSelectionRef.current,
      });

      if (
        next.length === prev.length &&
        next.every((key, index) => key === prev[index])
      ) {
        return prev;
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerKeysSignature, compareBy]);

  // Lista pequena: se ainda não há seleção, preenche com quem tem dado
  useEffect(() => {
    if (manualSelectionRef.current) return;
    if (pickerOptions.length > MAX_COMPARE_SERIES) return;

    setSelectedKeys((prev) => {
      if (prev.length > 0) return prev;

      const validKeys = pickerOptions.map((item) => item.key);
      const keysWithData = validKeys.filter((key) => {
        if (!data?.length) return false;
        if (compareBy === COMPARE_BY_EQUIPMENT) {
          return data.some((item) =>
            serialsMatch(getMeasurementSerial(item), key)
          );
        }
        return data.some(
          (item) => resolveCompanyId(item, equipmentOptions) === key
        );
      });

      const preferred = keysWithData.length ? keysWithData : validKeys;
      return preferred.slice(0, MAX_COMPARE_SERIES);
    });
  }, [data, pickerOptions, compareBy, equipmentOptions]);
  const buildCompareCharts = (source) => {
    const labelSet = [];
    const labelIndex = new Map();

    source.forEach((item) => {
      if (!item?.date) return;
      const label = formatLabel(item.date);
      if (!labelIndex.has(label)) {
        labelIndex.set(label, labelSet.length);
        labelSet.push(label);
      }
    });

    const groups = new Map();

    source.forEach((item) => {
      let groupKey = null;

      if (compareBy === COMPARE_BY_EQUIPMENT) {
        const serial = getMeasurementSerial(item);
        groupKey = selectedKeys.find((key) => serialsMatch(serial, key)) || null;
      } else {
        const companyId = resolveCompanyId(item, equipmentOptions);
        groupKey = companyId && selectedKeys.includes(companyId) ? companyId : null;
      }

      if (!groupKey) return;
      if (!groups.has(groupKey)) groups.set(groupKey, []);
      groups.get(groupKey).push(item);
    });

    if (!groups.size || !labelSet.length) {
      setHumidity(null);
      setTemperature(null);
      setPh(null);
      setOptionshumidity(null);
      setOptionsTemperature(null);
      setOptionsph(null);
      return;
    }

    const metadataByLabel = buildMetadataLabelsMap(source, formatLabel);
    const metadataPointStyleProps = buildMetadataPointStylePropsForLabels(
      labelSet,
      metadataByLabel
    );

    const makeDatasets = (valueGetter, { labelSuffix = "", valueSuffix = "" } = {}) =>
      selectedKeys
        .filter((key) => groups.has(key))
        .map((key, index) => {
          const picker = pickerOptions.find((item) => item.key === key);
          const items = groups.get(key) || [];
          const valuesByLabel = new Map();

          items.forEach((item) => {
            const label = formatLabel(item.date);
            const value = parseMeasurementNumber(valueGetter(item));
            if (value === null) return;

            if (!valuesByLabel.has(label)) valuesByLabel.set(label, []);
            valuesByLabel.get(label).push(value);
          });

          const seriesLabel = picker?.label || key;
          const color = picker?.color || getEquipmentColor(index);

          return {
            label: labelSuffix ? `${seriesLabel} · ${labelSuffix}` : seriesLabel,
            data: labelSet.map((label) =>
              averageNumbers(valuesByLabel.get(label) || [])
            ),
            borderColor: color,
            backgroundColor: color,
            borderWidth: 2,
            fill: false,
            tension: 0.2,
            spanGaps: true,
            datalabels: buildCompareDataLabels(valueSuffix, color),
            ...metadataPointStyleProps,
          };
        });

    const humidityDatasets = makeDatasets(
      getHumidityValue,
      { labelSuffix: "Umidade", valueSuffix: "%" }
    );
    const humidityValues = humidityDatasets.flatMap((ds) => ds.data);
    const tooltipMeta = {
      chartLabels: labelSet,
      sourceData: source,
      curvaSelecionada,
    };
    setHumidity({ labels: labelSet, datasets: humidityDatasets });
    setOptionshumidity(buildChartOptions(humidityValues, "%", tooltipMeta));

    const temperatureDatasets = makeDatasets(getTemperatureValue, {
      labelSuffix: "Temperatura",
      valueSuffix: " °C",
    });
    const temperatureValues = temperatureDatasets.flatMap((ds) => ds.data);
    setTemperature({ labels: labelSet, datasets: temperatureDatasets });
    setOptionsTemperature(
      buildChartOptions(temperatureValues, " °C", tooltipMeta)
    );

    const isDensity =
      source.length &&
      source[source.length - 1]?.unidadePh?.includes("Kg/m³");
    const phTitle = isDensity ? "Densidade" : "pH";
    const phValueSuffix = isDensity ? " Kg/m³" : "";
    const phDatasets = makeDatasets((item) => item?.ph, {
      labelSuffix: phTitle,
      valueSuffix: phValueSuffix,
    });
    const phValues = phDatasets.flatMap((ds) => ds.data);
    setPh({ labels: labelSet, datasets: phDatasets });
    setOptionsph(buildChartOptions(phValues, phValueSuffix, tooltipMeta));
  };

  useEffect(() => {
    if (!filteredData.length) {
      setHumidity(null);
      setTemperature(null);
      setPh(null);
      setOptionshumidity(null);
      setOptionsTemperature(null);
      setOptionsph(null);
      return;
    }

    buildCompareCharts(filteredData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData, compareBy, selectedKeys, pickerOptions, equipmentOptions, curvaSelecionada]);

  const plugins = {
    afterDraw: function (chart) {
      if (
        chart.data.datasets.length > 0 &&
        chart.data.datasets[0].data.length === 0
      ) {
        const width = chart.width;
        const height = chart.height;
        const ctx = chart.ctx;
        ctx.restore();
        const fontSize = (height / 160).toFixed(2);
        ctx.font = `${fontSize}em sans-serif`;
        ctx.textBaseline = "top";
        const text = "Nenhuma Informação Disponível";
        const textX = Math.round((width - ctx.measureText(text).width) / 2);
        const textY = height / 2;
        ctx.fillText(text, textX, textY);
        ctx.save();
      }
    },
  };

  const handleToggleKey = (key) => {
    setSelectedKeys((prev) => {
      let next;
      if (prev.includes(key)) {
        next = prev.filter((item) => item !== key);
      } else if (prev.length >= MAX_COMPARE_SERIES) {
        return prev;
      } else {
        next = [...prev, key];
      }
      manualSelectionRef.current = next.length > 0;
      return next;
    });
  };

  const headerRight = (
    <span className="dash-section-card__header-meta">
      {curvaSelecionada || "TODAS"}
      {hours?.start ? ` · ${hours.start} — ${hours.end || "--"}` : ""}
    </span>
  );

  const hasPhData = ph?.datasets?.some((ds) =>
    ds.data?.some((value) => value !== null && value !== undefined && !isNaN(value))
  );
  const hasTemperatureData = temperature?.datasets?.some((ds) =>
    ds.data?.some((value) => value !== null && value !== undefined && !isNaN(value))
  );

  const pickerTitle =
    compareBy === COMPARE_BY_UNIT
      ? "Escolha até 6 unidades"
      : "Escolha até 6 equipamentos";

  return (
    <div className="dash-graficos-section w-100">
      <DashMedidasSectionCard
        icon={FaChartLine}
        subtitle="Comparativo"
        title="Medições e Gráficos"
        headerRight={headerRight}
        collapsible
        sectionId="relatorios-graficos"
      >
        <div className="dash-section-card__content-stack">
          {filterContent}

          <div className="relatorios-chart-toolbar">
            <Form.Select
              size="sm"
              className="relatorios-chart-toolbar__curve"
              value={curvaSelecionada || ""}
              onChange={(event) => {
                if (typeof onCurvaChange === "function") {
                  onCurvaChange(event.target.value);
                }
              }}
              aria-label="Selecionar curva"
              disabled={!curveOptions.length}
            >
              <option value="">Todas as curvas</option>
              {curveOptions.map((curve) => (
                <option key={curve} value={curve}>
                  {curve}
                </option>
              ))}
            </Form.Select>

            <Form.Select
              size="sm"
              className="relatorios-chart-toolbar__model"
              value={modelFilter}
              onChange={(event) => setModelFilter(event.target.value)}
              aria-label="Filtrar por modelo"
            >
              <option value="">Todos os modelos</option>
              {availableModels.map((model) => (
                <option key={model.code} value={model.code}>
                  {model.label}
                </option>
              ))}
            </Form.Select>

            <DashMedidasButtonGroup>
              <DashMedidasButton
                variant={
                  compareBy === COMPARE_BY_EQUIPMENT ? "success" : "outline-success"
                }
                onClick={() => setCompareBy(COMPARE_BY_EQUIPMENT)}
              >
                Equipamentos
              </DashMedidasButton>
              <DashMedidasButton
                variant={
                  compareBy === COMPARE_BY_UNIT ? "success" : "outline-success"
                }
                onClick={() => setCompareBy(COMPARE_BY_UNIT)}
              >
                Unidades
              </DashMedidasButton>
            </DashMedidasButtonGroup>
          </div>

          <div className="relatorios-compare-picker">
            <div className="relatorios-compare-picker__head">
              <span>
                {pickerTitle}
                {modelFilter ? ` · ${modelFilter}` : ""}
              </span>
              <span className="relatorios-compare-picker__count">
                {selectedKeys.length}/{MAX_COMPARE_SERIES}
              </span>
            </div>
            <div className="relatorios-compare-picker__list">
              {pickerOptions.length === 0 ? (
                <span className="text-secondary small">
                  Nada disponível na seleção atual.
                </span>
              ) : (
                pickerOptions.map((item) => {
                  const checked = selectedKeys.includes(item.key);
                  const disabled =
                    !checked && selectedKeys.length >= MAX_COMPARE_SERIES;
                  return (
                    <label
                      key={item.key}
                      className={[
                        "relatorios-compare-picker__item",
                        checked ? "is-active" : "",
                        disabled ? "is-disabled" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => handleToggleKey(item.key)}
                      />
                      <span
                        className="relatorios-compare-picker__dot"
                        style={{ backgroundColor: item.color }}
                        aria-hidden
                      />
                      <span className="relatorios-compare-picker__label">
                        {item.label}
                        {compareBy === COMPARE_BY_UNIT && item.count
                          ? ` (${item.count})`
                          : ""}
                        {compareBy === COMPARE_BY_EQUIPMENT && item.meta
                          ? ` · ${item.meta}`
                          : ""}
                      </span>
                    </label>
                  );
                })
              )}
            </div>
          </div>

          {!data?.length ? (
            <Alert variant="warning" className="mb-0 relatorios-empty-alert">
              {hasEquipmentSelection
                ? "Sem dados para o período/filtro atual."
                : "Escolha uma ou mais Unidades para realizar a consulta."}
            </Alert>
          ) : !selectedKeys.length ? (
            <Alert variant="warning" className="mb-0 relatorios-empty-alert">
              Selecione itens para comparar.
            </Alert>
          ) : !filteredData.length ? (
            <Alert variant="warning" className="mb-0 relatorios-empty-alert">
              {compareBy === COMPARE_BY_UNIT
                ? "As unidades selecionadas não possuem medições neste filtro. Marque uma unidade que tenha dados."
                : "Os equipamentos selecionados não possuem medições neste filtro. Marque um equipamento que tenha dados."}
            </Alert>
          ) : (
            <>
              <div className="hidden-mobile relatorios-charts-stack">
                {humidity && optionshumidity ? (
                  <div
                    className="relatorios-chart-canvas"
                    style={{ height: CHART_HEIGHT_DESKTOP }}
                  >
                    <Line
                      data={humidity}
                      options={optionshumidity}
                      plugins={[plugins, ChartDataLabels]}
                      ref={chartRef1}
                    />
                  </div>
                ) : null}
                {temperature && optionsTemperature && hasTemperatureData ? (
                  <div
                    className="relatorios-chart-canvas"
                    style={{ height: CHART_HEIGHT_DESKTOP }}
                  >
                    <Line
                      data={temperature}
                      options={optionsTemperature}
                      plugins={[plugins, ChartDataLabels]}
                    />
                  </div>
                ) : null}
                {ph && optionsph && hasPhData ? (
                  <div
                    className="relatorios-chart-canvas"
                    style={{ height: CHART_HEIGHT_DESKTOP }}
                  >
                    <Line
                      data={ph}
                      options={optionsph}
                      plugins={[plugins, ChartDataLabels]}
                    />
                  </div>
                ) : null}
              </div>

              <div className="block-mobile relatorios-charts-stack">
                {humidity && optionshumidity ? (
                  <div
                    className="relatorios-chart-canvas"
                    style={{ height: CHART_HEIGHT_MOBILE }}
                  >
                    <Line
                      data={humidity}
                      options={optionshumidity}
                      plugins={[plugins, ChartDataLabels]}
                    />
                  </div>
                ) : null}
                {temperature && optionsTemperature && hasTemperatureData ? (
                  <div
                    className="relatorios-chart-canvas"
                    style={{ height: CHART_HEIGHT_MOBILE }}
                  >
                    <Line
                      data={temperature}
                      options={optionsTemperature}
                      plugins={[plugins, ChartDataLabels]}
                    />
                  </div>
                ) : null}
                {ph && optionsph && hasPhData ? (
                  <div
                    className="relatorios-chart-canvas"
                    style={{ height: CHART_HEIGHT_MOBILE }}
                  >
                    <Line
                      data={ph}
                      options={optionsph}
                      plugins={[plugins, ChartDataLabels]}
                    />
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>
      </DashMedidasSectionCard>
    </div>
  );
};

export default Grafico;
